import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, FileText, Users, Building, Clock, Shield, MessageCircle, ChevronRight, HelpCircle } from "lucide-react";
import ContactForm from "../components/ContactForm";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20with%20Business%20Loan%20Assistance%20in%20Coimbatore";

const loanTypes = [
  {
    title: "Working Capital Loans",
    description: "Short-term financing to manage day-to-day operational expenses such as salaries, inventory, and rent. Ideal for businesses in Coimbatore facing seasonal demand fluctuations or cash flow gaps.",
  },
  {
    title: "Term Loans",
    description: "Medium to long-term loans for business expansion, new projects, or large capital expenditure. Fixed repayment schedules with competitive interest rates from leading banks.",
  },
  {
    title: "MSME Loans",
    description: "Government-backed loans specifically designed for Micro, Small, and Medium Enterprises. Access schemes like CGTMSE, PMEGP, and MUDRA with subsidized interest rates and minimal collateral.",
  },
  {
    title: "Startup Funding",
    description: "Funding support for new businesses and startups in Coimbatore. We help you access Startup India benefits, seed funding, and early-stage business loans with relaxed eligibility norms.",
  },
  {
    title: "Machinery & Equipment Loans",
    description: "Dedicated financing for purchasing new machinery, equipment, or technology upgrades. Especially beneficial for Coimbatore's thriving manufacturing and textile sectors.",
  },
  {
    title: "Overdraft & Cash Credit",
    description: "Flexible credit facilities that allow you to withdraw funds as needed, up to an approved limit. Pay interest only on the amount utilized, giving your business financial flexibility.",
  },
];

const eligibility = [
  { title: "Proprietorship Businesses", desc: "Sole proprietors with valid GST registration and 1+ year of business operations" },
  { title: "Partnership Firms", desc: "Registered partnership firms with a valid partnership deed and business track record" },
  { title: "Private Limited Companies", desc: "Incorporated companies with audited financials and a clear business plan" },
  { title: "GST-Registered MSMEs", desc: "Micro, Small, and Medium Enterprises with Udyam registration and GST compliance" },
  { title: "Local Coimbatore Businesses", desc: "Business owners operating in Coimbatore and surrounding areas of Tamil Nadu" },
];

const documents = [
  "PAN Card & Aadhaar Card of the business owner/directors",
  "GST Registration Certificate",
  "Bank Statements (6 to 12 months)",
  "Income Tax Returns (ITR) for 1 to 2 years",
  "Business address proof (utility bill, rent agreement, or property documents)",
  "Business registration documents (Certificate of Incorporation, Partnership Deed, MSME/Udyam Certificate)",
  "Financial statements (Balance Sheet & Profit & Loss for 2 years)",
];

const processSteps = [
  { number: "01", title: "Enquiry Submission", desc: "Fill out our contact form or reach us via WhatsApp with your loan requirement and basic business details." },
  { number: "02", title: "Eligibility Assessment", desc: "Our experts evaluate your business profile, turnover, credit history, and loan eligibility to identify the best options." },
  { number: "03", title: "Documentation Review", desc: "We review and organize all required documents, ensuring everything meets lender requirements for a smooth process." },
  { number: "04", title: "Loan Application Coordination", desc: "We prepare and submit your loan application to the most suitable banks and NBFCs, handling all communication on your behalf." },
  { number: "05", title: "Follow-Up with Lenders", desc: "Dedicated follow-up with lending institutions until approval, disbursement, and any post-disbursement formalities are completed." },
];

const faqs = [
  {
    question: "How to apply for a business loan in Coimbatore?",
    answer: "You can apply by contacting WINTRUST SOLUTIONS through our website contact form, WhatsApp, or phone. We'll assess your eligibility, help you gather required documents, and coordinate with banks and NBFCs to get the best loan offer for your business.",
  },
  {
    question: "What is the minimum turnover required for a business loan?",
    answer: "The minimum turnover varies by lender and loan type. Generally, banks require an annual turnover of Rs. 10-25 lakhs for unsecured business loans. For MSME and MUDRA loans, the threshold can be lower. Our team will help you find a lender that matches your business profile.",
  },
  {
    question: "Can new businesses in Coimbatore get loans?",
    answer: "Yes! Several government schemes like MUDRA, CGTMSE, and Startup India specifically support new businesses. Even businesses with less than 1 year of operation can access funding through these schemes. We specialize in helping Coimbatore startups navigate these options.",
  },
  {
    question: "Is GST registration mandatory for a business loan?",
    answer: "While not always mandatory for very small loans (like MUDRA Shishu up to Rs. 50,000), most banks prefer GST-registered businesses for loans above Rs. 2-5 lakhs. GST registration strengthens your loan application and improves approval chances significantly.",
  },
  {
    question: "How long does business loan approval take?",
    answer: "Approval timelines vary: MUDRA loans can be approved in 7-10 working days, regular business loans take 10-15 working days, and larger term loans may take 3-4 weeks. With WINTRUST's documentation support, we ensure there are no delays from incomplete paperwork.",
  },
  {
    question: "What is the interest rate range for business loans in Coimbatore?",
    answer: "Business loan interest rates typically range from 8% to 16% per annum depending on the loan type, lender, business profile, and credit score. Government-backed MSME loans often have subsidized rates starting from 8-10%. We help you compare offers to get the best rate.",
  },
];

const BusinessLoanPage = () => {
  useCanonical("/business-loan-assistance");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Business Loan Assistance in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Expert business loan assistance for MSMEs, startups, and businesses in Coimbatore. End-to-end documentation and bank coordination support.",
  });

  return (
    <>
      <Helmet>
        <title>Business Loan Assistance in Coimbatore | WinTrust</title>
        <meta name="description" content="Get expert business loan assistance in Coimbatore for MSME, working capital & startup funding. End-to-end documentation & bank coordination support." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-16 md:py-20">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-6">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">Business Loan Assistance</span>
          </nav>
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-6" data-testid="business-loan-h1">
              Business Loan Assistance in Coimbatore for MSMEs & Startups
            </h1>
            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
              Securing a business loan shouldn't be stressful. At WINTRUST SOLUTIONS, we assist business owners across Coimbatore and Tamil Nadu in accessing the right financing for their needs. Whether you're seeking an <strong className="text-white">MSME loan in Coimbatore</strong>, a <strong className="text-white">working capital loan</strong> to manage cash flow, or <strong className="text-white">startup loan support in Coimbatore</strong> to launch your venture, our team provides end-to-end guidance. From eligibility assessment and 100% documentation support to application coordination with banks and NBFCs, we handle every step so you can focus on growing your business. Our personalized advisory approach ensures you get the best loan product at competitive rates, tailored to your unique business requirements.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary flex items-center gap-2" data-testid="hero-apply-btn">
                Apply Now <ChevronRight size={18} />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={18} />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Business Loans */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
            Types of Business Loans We Assist With in Coimbatore
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            We help Coimbatore businesses access a wide range of loan products from leading banks, NBFCs, and government schemes.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loanTypes.map((loan, i) => (
              <div key={i} className="p-6 rounded-xl border border-slate-200 hover:border-gold/30 hover:shadow-md transition-all" data-testid={`loan-type-${i}`}>
                <h3 className="text-lg font-semibold text-navy mb-3 font-heading">{loan.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{loan.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Can Apply */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
            Who Can Apply for Business Loan?
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Business loan eligibility varies by lender, but generally the following entities operating in Coimbatore and Tamil Nadu can apply:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {eligibility.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-navy text-sm">{item.title}</h3>
                  <p className="text-slate-500 text-xs mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
              Documents Required for Business Loan in Coimbatore
            </h2>
            <p className="text-slate-600 text-center mb-10">
              Keep the following documents ready for a smooth and fast loan application process:
            </p>
            <div className="space-y-3">
              {documents.map((doc, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <FileText className="text-gold flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-navy text-sm">{doc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
            Our Business Loan Assistance Process
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Our streamlined 5-step process ensures quick and hassle-free loan assistance for businesses in Coimbatore.
          </p>
          <div className="max-w-3xl mx-auto space-y-6">
            {processSteps.map((step, i) => (
              <div key={i} className="flex gap-5 items-start" data-testid={`process-step-${i}`}>
                <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-gold font-bold text-sm">{step.number}</span>
                </div>
                <div className="pt-1">
                  <h3 className="text-lg font-semibold text-navy font-heading">{step.title}</h3>
                  <p className="text-slate-600 text-sm mt-1">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-4 font-heading text-center">
            Why Choose WinTrust Solutions in Coimbatore?
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Trusted by businesses across Coimbatore and Tamil Nadu for expert financial advisory and loan assistance.
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              { icon: <Users size={24} />, title: "Personalized Financial Advisory", desc: "Every business is unique. We analyze your specific needs and recommend the best loan products suited to your business model and growth plans." },
              { icon: <FileText size={24} />, title: "End-to-End Documentation Guidance", desc: "From collecting documents to organizing and submitting applications, we handle everything so you don't have to worry about paperwork." },
              { icon: <Shield size={24} />, title: "Transparent Communication", desc: "No hidden fees, no surprises. We keep you informed at every stage of the loan application process with clear timelines and expectations." },
              { icon: <Clock size={24} />, title: "Dedicated Support", desc: "A single point of contact for all your queries. Our team follows up with lenders proactively and keeps you updated until disbursement." },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-6 bg-slate-50 rounded-xl border border-slate-200">
                <div className="w-12 h-12 bg-navy/5 rounded-lg flex items-center justify-center flex-shrink-0 text-gold">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-navy mb-1 font-heading">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-navy mb-4 font-heading">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Common questions about business loan assistance in Coimbatore
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-lg border border-slate-200 px-6"
                  data-testid={`loan-faq-${index}`}
                >
                  <AccordionTrigger className="text-left text-navy font-medium py-4 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-navy section-padding">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 font-heading">
            Apply for Business Loan in Coimbatore Today
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Get expert assistance for MSME loans, working capital, startup funding, and more. Our team in Coimbatore is ready to help you secure the right financing.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#contact" className="btn-primary flex items-center gap-2" data-testid="cta-apply-btn">
              Contact Us Now <ChevronRight size={18} />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
            >
              <MessageCircle size={18} />
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />

      {/* Cross-link to Personal Loan */}
      <section className="bg-slate-50 py-10">
        <div className="container-custom text-center">
          <p className="text-slate-600 mb-3">Looking for a personal loan instead?</p>
          <Link to="/personal-loan-assistance" className="text-gold font-semibold hover:underline inline-flex items-center gap-1">
            Personal Loan Assistance in Coimbatore <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
};

export default BusinessLoanPage;
